import { isAnyOf } from "@reduxjs/toolkit";

import { readBriefing } from "@intelligems/ig-types/schemas/ai/ui-actions/audit-analyses";
import type { AppListenerMiddleware, RootState } from "@src/store/rootState";
import { getOrganization } from "@src/store/slices/organization";
import { auditChatAction } from "@src/store/slices/opportunityAudit/chat";
import {
  auditReset,
  briefingPresented,
  briefingRegionLanded,
  briefingRegionRevealed,
  getAtlasConnection,
  getBriefingArrived,
  getBriefingRevealed,
  getRecommendationStatuses,
  recommendationsPresented,
} from "@src/store/slices/opportunityAudit";
import { setItemOf } from "@src/store/slices/opportunityAudit/recommendationSetItem";
import type { BriefingRegion } from "@src/store/slices/opportunityAudit/types";
import { resetAtlas } from "@src/store/slices/opportunityAudit/thunks/atlasAgent";
import {
  loadRecommendationStatuses,
  recommendationUpdated,
} from "@src/store/slices/opportunityAudit/thunks/recommendationStatuses";
import { sendWebsocketMessage } from "@src/store/slices/global";

/**
 * Watches the chat stream for the audit's ui-actions and folds them into the
 * slice, so the panel beside the chat renders from redux rather than parsing
 * the message stream itself.
 *
 * Listens to the audit's own chat, which the shared chat's listener never
 * sees.
 */
/** Panel order, which is also reveal order for regions that arrive together. */
const REGION_ORDER: BriefingRegion[] = [
  "storeFacts",
  "brand",
  "summary",
  "funnel",
];

/** The least time between two regions appearing, so each gets its own moment. */
export const REVEAL_INTERVAL_MS = 1000;

export const setupOpportunityAuditListener = (
  middleware: AppListenerMiddleware
) => {
  // Regions arrive in bursts, often three at once. Each waits its turn here.
  let queued: BriefingRegion[] = [];
  let nextRevealAt = 0;
  // A reset outdates every reveal still waiting.
  let generation = 0;

  middleware.startListening({
    actionCreator: auditReset,
    effect: () => {
      queued = [];
      nextRevealAt = 0;
      generation += 1;
    },
  });

  middleware.startListening({
    matcher: isAnyOf(briefingRegionLanded, briefingPresented),
    effect: async (_action, { dispatch, getState, delay }) => {
      const arrived = getBriefingArrived(getState());
      const revealed = getBriefingRevealed(getState());
      const fresh = REGION_ORDER.filter(
        (region) =>
          arrived[region] !== null &&
          !revealed.includes(region) &&
          !queued.includes(region)
      );
      queued.push(...fresh);

      // A replayed briefing was already seen, so it shows at once.
      if (getAtlasConnection(getState()).cursor.replaying) {
        for (const region of fresh) {
          dispatch(briefingRegionRevealed(region));
        }
        return;
      }

      // Every slot is booked before the first wait, so a later burst queues
      // behind this one rather than between its regions.
      const slots = fresh.map((region) => {
        const at = Math.max(Date.now(), nextRevealAt);
        nextRevealAt = at + REVEAL_INTERVAL_MS;
        return { region, at };
      });
      const booked = generation;

      for (const { region, at } of slots) {
        await delay(Math.max(0, at - Date.now()));
        if (generation !== booked) {
          return;
        }
        queued = queued.filter((it) => it !== region);
        dispatch(briefingRegionRevealed(region));
      }
    },
  });

  // A recommendation's saved record changed: its state or its test.
  middleware.startListening({
    actionCreator: sendWebsocketMessage,
    effect: (action, { dispatch }) => {
      if (action.payload.type === "auditRecommendationUpdated") {
        void dispatch(recommendationUpdated(action.payload.status));
      }
    },
  });

  // The conversation is about one store. Switching org only resets; the panel
  // then opens that store's own latest audit.
  middleware.startListening({
    predicate: (_action, state: RootState, prev: RootState) => {
      const before = getOrganization(prev)?.id;
      return before !== undefined && getOrganization(state)?.id !== before;
    },
    effect: (_action, { dispatch }) => {
      void dispatch(resetAtlas());
    },
  });

  middleware.startListening({
    actionCreator: auditChatAction,
    effect: (action, { dispatch, getState }) => {
      const streamActions = Array.isArray(action.payload)
        ? action.payload
        : [action.payload];

      for (const streamAction of streamActions) {
        if (
          streamAction.type !== "addStreamPart" ||
          streamAction.streamPart.type !== "tool-output-available"
        ) {
          continue;
        }

        const call = streamAction.streamPart.toolCallId;
        const results = readUiAction(streamAction.streamPart.output);
        if (!results) {
          continue;
        }

        if (isRecommendations(results)) {
          const items = results.recommendations.map(
            (recommendation, position) =>
              setItemOf(recommendation, call, position)
          );
          dispatch(recommendationsPresented({ call, items }));
          // A live set's recommendations whose status did not come with them. On replay
          // every record is loaded once the replay ends.
          if (!getAtlasConnection(getState()).cursor.replaying) {
            const held = getRecommendationStatuses(getState());
            const missing = items
              .map((item) => item.key)
              .filter((key) => !(key in held));
            void dispatch(loadRecommendationStatuses(missing));
          }
        } else if (isBriefing(results)) {
          dispatch(
            briefingPresented({
              call,
              briefing: readBriefing(results.briefing),
            })
          );
        }
      }
    },
  });
};

/** Only the fields read here; each recommendation and region is read at today's version. */
type AuditUiAction =
  | {
      type: "ui-action";
      action: "presentAuditRecommendation";
      recommendations: unknown[];
    }
  | { type: "ui-action"; action: "presentAuditBriefing"; briefing: unknown };

/**
 * Tool outputs arrive wrapped as `{ value: { results } }`. Everything is
 * checked rather than cast: this is model-driven input reaching redux, and a
 * malformed payload should be ignored, not crash the page.
 */
function readUiAction(output: unknown): AuditUiAction | null {
  if (typeof output !== "object" || output === null) {
    return null;
  }
  const value = (output as { value?: unknown }).value;

  if (typeof value !== "object" || value === null) {
    return null;
  }
  const results = (value as { results?: unknown }).results;

  if (typeof results !== "object" || results === null) {
    return null;
  }
  const candidate = results as { type?: unknown };

  return candidate.type === "ui-action" ? (results as AuditUiAction) : null;
}

function isRecommendations(
  results: AuditUiAction
): results is Extract<AuditUiAction, { action: "presentAuditRecommendation" }> {
  return (
    results.action === "presentAuditRecommendation" &&
    Array.isArray(results.recommendations) &&
    results.recommendations.length > 0
  );
}

/**
 * A briefing is legal with every region empty — a store we could read nothing
 * from still has a briefing, and the panel says so. So this checks the shape
 * is there, not that anything is in it.
 */
function isBriefing(
  results: AuditUiAction
): results is Extract<AuditUiAction, { action: "presentAuditBriefing" }> {
  if (results.action !== "presentAuditBriefing") {
    return false;
  }

  const briefing = results.briefing as unknown;
  if (typeof briefing !== "object" || briefing === null) {
    return false;
  }

  return "storeFacts" in briefing && "brand" in briefing;
}
